import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';

import { setupStickyHeader } from './sticky-header';

// Horizontal overflow is clipped/scrolled here (same as pre-floatThead-removal
// UX). That makes this Paper a sticky containing block, so native
// position:sticky cannot pin headers to the viewport while the page scrolls —
// setupStickyHeader() uses a fixed thead clone instead (see sticky-header.js).
const StyledPaper = styled(Paper)(({ theme }) => {
  return {
    width: '100%',
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    overflowX: 'auto',
    // Prefer visible vertically so the page (not this Paper) is the vertical
    // scrollport; browsers may still compute this to auto when overflow-x is
    // not visible — sticky-header.js does not rely on CSS sticky either way.
    overflowY: 'visible',
  };
});

const StyledTable = styled(Table)({
  // Default floor for wide projection tables. List panels that need to stay
  // within a clipped card should pass tableProps.sx.minWidth: 0 (and usually
  // tableLayout: 'fixed') so long nowrap cells ellipsis instead of forcing
  // horizontal overflow.
  minWidth: 700,
});

const StickyHeaderTable = ({
  stickyHeader = true,
  tableProps = {},
  children,
}: {
  stickyHeader?: boolean;
  tableProps?: {
    sx?: React.ComponentProps<typeof Table>['sx'];
    [key: string]: unknown;
  };
  children?: React.ReactNode;
}) => {
  const containerRef = useRef(null);
  const tableRef = useRef(null);

  useEffect(() => {
    if (stickyHeader !== true) {
      return undefined;
    }
    return setupStickyHeader(containerRef.current, tableRef.current);
  }, [stickyHeader, children]);

  const { sx: tableSx, ...restTableProps } = tableProps;

  return (
    <StyledPaper className="table-responsive" ref={containerRef}>
      <StyledTable
        className="sticky-header-table"
        ref={tableRef}
        sx={tableSx}
        {...restTableProps}
      >
        {children}
      </StyledTable>
    </StyledPaper>
  );
};

StickyHeaderTable.propTypes = {
  stickyHeader: PropTypes.bool.isRequired,
  tableProps: PropTypes.object,
  children: PropTypes.node,
};

export default StickyHeaderTable;
